import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import MobileMenu from '../../components/feature/MobileMenu';
import { useSEO } from '../../hooks/useSEO';
import JsonLd from '../../components/feature/JsonLd';

export default function AboutPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useSEO({
    title: 'About Us | Human-Centred Design Experts | Our Story & Approach',
    description: 'Learn about our human-centred design consultancy based in London. We combine research, strategy, and creativity to solve complex challenges in healthcare, government, and social sectors.',
    keywords: 'about us, design consultancy, human-centred design team, innovation experts, London design agency, design thinking approach',
    canonical: '/about',
    ogType: 'website',
    ogImage: 'https://public.readdy.ai/ai/img_res/3e21399fe5b9a0e79fd9bffb4c0f637b.jpg'
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": "About Design For",
        "url": "https://example.com/about",
        "description": "Learn about our human-centred design consultancy based in London",
        "mainEntity": {
          "@type": "Person",
          "name": "Kara Prosser",
          "jobTitle": "Founder",
          "worksFor": {
            "@type": "Organization",
            "name": "Design For"
          },
          "description": "Experience designer, founder of Design For, with 15+ years across 40+ countries redesigning experiences in healthcare, government, tech, and corporates",
          "sameAs": [
            "https://www.linkedin.com/in/kara-prosser-26960977/"
          ]
        }
      }} />
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-24">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/511aeba7affe24966a38635cdd087c13.png" 
                alt="Design For" 
                className="h-16"
              />
            </Link>
            <div className="hidden md:flex items-center space-x-10">
              <Link to="/" className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Home</Link>
              <Link to="/about" className="text-primary hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">About</Link>
              <Link to="/services" className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Services</Link>
              <Link to="/work" className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Work</Link>
              <Link to="/resources" className="text-black hover:text-[#ff4858] transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Insights</Link>
              <Link to="/contact" className="bg-primary text-white px-6 py-3 rounded-full hover:bg-primary/90 transition-all duration-200 text-sm font-bold uppercase tracking-wider whitespace-nowrap">
                Contact
              </Link>
            </div>
            <button 
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden text-black"
            >
              <i className="ri-menu-line text-3xl"></i>
            </button>
          </div>
        </div>
      </nav>

      <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Hero Section - Mission Statement */}
      <section className="min-h-screen flex items-center relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/3225b8391bf9aaae4aff6a1a542fc2cf.jpeg"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        {/* Overlay */}
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-32 pt-48">
          <h1 className="text-5xl lg:text-7xl font-black text-white tracking-tight leading-[1.05] max-w-5xl">
            Design For exists to design experiences that create real value for humans, while driving <span className="text-white">commercial growth.</span>
          </h1>
        </div>
      </section>

      {/* Kara Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 xl:gap-24 items-start">
            {/* Left - Photo */}
            <div className="relative">
              <div className="relative w-full h-[680px] lg:h-[780px] rounded-2xl overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/0b17fafc34a30d385e9f5af6b7d35de7.jpeg" 
                  alt="Kara - Founder of Design For" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Right - Bio Content */}
            <div className="space-y-8 lg:pt-4">
              <h2 className="text-5xl lg:text-6xl font-black text-black tracking-tight">
                Hey, I&apos;m Kara!
              </h2>
              
              <p className="text-xl lg:text-2xl text-black font-bold leading-relaxed">
                Experience designer, founder of Design For, Aussie in London — and a firm believer that no matter where we come from, there&apos;s one thing that unites us… the need for better experiences!
              </p>

              <div className="space-y-6 text-base lg:text-lg text-gray-600 leading-relaxed font-medium">
                <p>
                  In 2014 I lost my dad suddenly and saw first hand how broken our health systems are. Nothing had been designed for humans. Since then, I&apos;ve spent 15+ years across 40+ countries redesigning the experiences that matter most, from healthcare and government to tech, start-ups and big corporates in every sector.
                </p>
                <p>
                  I&apos;m a strategist, designer, facilitator, and storyteller. I bring the rare mix of 10+ years in digital agencies — where things ship fast and innovation thrives, and 3.5+ years in top-tier consulting — where strategy, rigour and commercial outcomes rule. I&apos;ve built global innovation businesses from scratch, scaled social impact into multi-million growth, and helped highly regulated organisations move at startup speed.
                </p>
                <p>
                  I&apos;m known for my energy, passion, and collaborative style. I inspire teams to think differently, listen deeply, and design experiences that genuinely improve lives. If you&apos;re solving something important, or want to, I&apos;d love to chat.
                </p>
              </div>

              <div className="pt-4">
                <Link to="/contact" className="bg-white text-primary border-2 border-primary px-10 py-4 rounded-full hover:bg-primary hover:text-white transition-all duration-300 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-3 whitespace-nowrap cursor-pointer">
                  Let&apos;s Chat
                  <i className="ri-arrow-right-line text-lg"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Design For Section */}
      <section className="py-28 bg-[#f8f8f8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-6xl lg:text-7xl font-black text-black tracking-tight mb-10">Why Design For</h2>
          
          <div className="space-y-6">
            {/* Reason 01 */}
            <div className="bg-white rounded-2xl p-6 lg:p-8 hover:shadow-lg transition-all duration-300">
              <div className="flex flex-col lg:flex-row gap-5 lg:gap-8 items-start">
                <p className="text-2xl font-black text-primary flex-shrink-0">01</p>
                <div className="space-y-2">
                  <h3 className="text-xl font-black text-black leading-tight">
                    Energy, empathy and alignment that moves organisations forward
                  </h3>
                  <p className="text-base text-gray-500 leading-relaxed font-medium">
                    Design For builds trust quickly across cultures, functions and seniority levels. With experience spanning 40+ countries, we unite diverse stakeholders, create clarity in complexity, and generate the momentum that turns strategy into action.
                  </p>
                </div>
              </div>
            </div>

            {/* Reason 02 */}
            <div className="bg-white rounded-2xl p-6 lg:p-8 hover:shadow-lg transition-all duration-300">
              <div className="flex flex-col lg:flex-row gap-5 lg:gap-8 items-start">
                <p className="text-2xl font-black text-primary flex-shrink-0">02</p>
                <div className="space-y-2">
                  <h3 className="text-xl font-black text-black leading-tight">
                    Commercial outcomes designed in from day one
                  </h3>
                  <p className="text-base text-gray-500 leading-relaxed font-medium">
                    Human-centred design isn&apos;t a feel-good exercise. It sharpens strategy, strengthens value propositions, improves customer loyalty and drives measurable growth. Insight becomes performance.
                  </p>
                </div>
              </div>
            </div>

            {/* Reason 03 */}
            <div className="bg-white rounded-2xl p-6 lg:p-8 hover:shadow-lg transition-all duration-300">
              <div className="flex flex-col lg:flex-row gap-5 lg:gap-8 items-start">
                <p className="text-2xl font-black text-primary flex-shrink-0">03</p>
                <div className="space-y-2">
                  <h3 className="text-xl font-black text-black leading-tight">
                    Confidence in complex, high-stakes environments
                  </h3>
                  <p className="text-base text-gray-500 leading-relaxed font-medium">
                    From highly regulated healthcare to global systems change, Design For brings structured thinking, calm leadership and cultural intelligence where stakes are high and decisions matter.
                  </p>
                </div>
              </div>
            </div>

            {/* Reason 04 */}
            <div className="bg-white rounded-2xl p-6 lg:p-8 hover:shadow-lg transition-all duration-300">
              <div className="flex flex-col lg:flex-row gap-5 lg:gap-8 items-start">
                <p className="text-2xl font-black text-primary flex-shrink-0">04</p>
                <div className="space-y-2">
                  <h3 className="text-xl font-black text-black leading-tight">
                    Strategy that lands and sticks
                  </h3>
                  <p className="text-base text-gray-500 leading-relaxed font-medium">
                    From future vision and business models to research, prototyping and delivery roadmaps, Design For works across the full arc, blending digital pace with consulting rigour so ideas are bold and executable.
                  </p>
                </div>
              </div>
            </div>

            {/* Reason 05 */}
            <div className="bg-white rounded-2xl p-6 lg:p-8 hover:shadow-lg transition-all duration-300">
              <div className="flex flex-col lg:flex-row gap-5 lg:gap-8 items-start">
                <p className="text-2xl font-black text-primary flex-shrink-0">05</p>
                <div className="space-y-2">
                  <h3 className="text-xl font-black text-black leading-tight">
                    Designing for impact beyond the balance sheet
                  </h3>
                  <p className="text-base text-gray-500 leading-relaxed font-medium">
                    The strongest businesses create value for customers, communities and society. Design For believes that when you design properly for people, commercial success and positive impact move together. Growth and good are not competing forces, they reinforce each other.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Design For Values Section */}
      <section className="py-28 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/e2ab97792e9c84aa085fcc949ab165ff.jpeg"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/55"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-6xl lg:text-7xl font-black text-white tracking-tight mb-16">Design For Values</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Value 01 */}
            <div className="bg-white rounded-2xl p-8 space-y-4 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <p className="text-4xl font-black text-primary">01</p>
              <h3 className="text-2xl font-black text-black">Humans First</h3>
              <p className="text-sm text-gray-500 leading-relaxed font-medium">
                If it doesn&apos;t work for real people, it doesn&apos;t work. We design with empathy, depth and respect for the lives behind the data.
              </p>
            </div>

            {/* Value 02 */}
            <div className="bg-white rounded-2xl p-8 space-y-4 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <p className="text-4xl font-black text-primary">02</p>
              <h3 className="text-2xl font-black text-black">Good is growth</h3>
              <p className="text-sm text-gray-500 leading-relaxed font-medium">
                Designing for humanity creates better businesses. Impact and commercial success move together, not apart.
              </p>
            </div>

            {/* Value 03 */}
            <div className="bg-white rounded-2xl p-8 space-y-4 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <p className="text-4xl font-black text-primary">03</p>
              <h3 className="text-2xl font-black text-black">Courage over comfort</h3>
              <p className="text-sm text-gray-500 leading-relaxed font-medium">
                We challenge lazy thinking, broken systems and &quot;that&apos;s how it&apos;s always been done.&quot; If something isn&apos;t right, we say it. If it needs changing, we push for it.
              </p>
            </div>

            {/* Value 04 */}
            <div className="bg-white rounded-2xl p-8 space-y-4 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <p className="text-4xl font-black text-primary">04</p>
              <h3 className="text-2xl font-black text-black">Trust the process</h3>
              <p className="text-sm text-gray-500 leading-relaxed font-medium">
                Great design takes rigour. Research, experimentation and iteration are part of the craft. We test, learn, refine and get comfortable in the messy middle where the real breakthroughs happen.
              </p>
            </div>

            {/* Value 05 */}
            <div className="bg-white rounded-2xl p-8 space-y-4 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <p className="text-4xl font-black text-primary">05</p>
              <h3 className="text-2xl font-black text-black">No egos</h3>
              <p className="text-sm text-gray-500 leading-relaxed font-medium">
                No hierarchy in curiosity. No room for arrogance. We respect each other&apos;s backgrounds, cultures and perspectives because diversity makes the work stronger. We also have fun!
              </p>
            </div>
          </div>

          {/* Get In Touch Button */}
          <div className="mt-16 text-center">
            <Link to="/contact" className="bg-white text-primary px-12 py-5 rounded-full hover:bg-white/90 transition-all duration-300 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-3 whitespace-nowrap cursor-pointer">
              Get In Touch
              <i className="ri-arrow-right-line text-lg"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img 
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png" 
                alt="Design For" 
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">Strategic consulting for ambitious businesses.</p>
            </div>

            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Navigate</h4>
              <div className="space-y-3">
                <Link to="/" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Home</Link>
                <Link to="/about" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">About</Link>
                <Link to="/services" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Services</Link>
                <Link to="/work" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Work</Link>
                <Link to="/resources" className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium">Insights</Link>
                <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Contact</Link>
              </div>
            </div>

            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Follow</h4>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</Link>
              <Link to="/terms" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</Link>
              <Link to="/cookies" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
